import type { ISbStoryData } from '@storyblok/react/rsc'

import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok'
import { storySlugFromPathname } from '@/utils/url'

/** Adviser story `content` fields read in this module */
type AdviserStoryContent = {
  settings?: unknown
  offices?: unknown
}

export type AdviserStory = ISbStoryData<AdviserStoryContent>

export type FetchOrderedAdvisersOptions = {
  pathname: string
  officeUuid: string
  location: string
  filterLocation: string
  blok: { tabContent?: unknown }
}

function searchParamsWithLocale() {
  return {
    ...searchParamsData,
    language: process.env.NEXT_PUBLIC_NEXT_LOCATION === 'en-nz' ? 'en-nz' : 'default',
  }
}

export function isAdviserDetailsBlok(blok: unknown): boolean {
  if (!blok || typeof blok !== 'object') return false
  const component = (blok as { component?: string }).component
  return (
    typeof component === 'string' && component.toLowerCase().replace(/_/g, '') === 'adviserdetails'
  )
}

function adviserIdsFromStoryblokListArray(raw: unknown[]): string[] | null {
  const out: string[] = []
  for (const entry of raw) {
    if (typeof entry === 'string') out.push(entry)
    else if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as { uuid?: string }).uuid === 'string'
    ) {
      out.push((entry as { uuid: string }).uuid)
    } else if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as { id?: string }).id === 'string'
    ) {
      const id = (entry as { id: string }).id
      if (id.length > 20) out.push(id)
    }
  }
  return out.length ? out : null
}

function adviserIdsFromDetailsNode(node: unknown): string[] | null {
  if (!isAdviserDetailsBlok(node)) return null
  const raw =
    (node as { advisers?: unknown; adviser?: unknown }).advisers ??
    (node as { adviser?: unknown }).adviser

  if (raw == null) return null

  if (typeof raw === 'string') {
    const ids = raw
      .split(/,+/)
      .map((s) => s.trim())
      .filter(Boolean)
    return ids.length ? ids : null
  }

  if (!Array.isArray(raw)) return null
  return adviserIdsFromStoryblokListArray(raw)
}

export function orderedAdviserIdsFromBlok(blok: { tabContent?: unknown }): string[] {
  const tabs = blok?.tabContent
  if (!Array.isArray(tabs)) return []
  for (const tab of tabs) {
    const inner = (tab as { tabContent?: unknown })?.tabContent
    if (!Array.isArray(inner) || !inner[0]) continue
    const ids = adviserIdsFromDetailsNode(inner[0])
    if (ids) return ids
  }
  return []
}

function orderedAdviserIdsFromPageBody(content: unknown): string[] {
  const walk = (node: unknown): string[] | null => {
    if (node == null) return null
    if (Array.isArray(node)) {
      for (const child of node) {
        const adviserIds = adviserIdsFromDetailsNode(child)
        if (adviserIds) return adviserIds
        if (child && typeof child === 'object') {
          const nestedResult = walk(child)
          if (nestedResult) return nestedResult
        }
      }
      return null
    }
    if (typeof node === 'object') {
      const adviserIds = adviserIdsFromDetailsNode(node)
      if (adviserIds) return adviserIds
      for (const key of Object.keys(node as object)) {
        const nestedResult = walk((node as Record<string, unknown>)[key])
        if (nestedResult) return nestedResult
      }
    }
    return null
  }

  if (!content || typeof content !== 'object') return []
  const body = (content as { body?: unknown }).body
  return Array.isArray(body) ? (walk(body) ?? []) : []
}

export async function fetchOrderedAdvisers(
  storyblokApi: NonNullable<ReturnType<typeof getStoryblokApi>>,
  opts: FetchOrderedAdvisersOptions
): Promise<AdviserStory[]> {
  const { location, filterLocation, blok } = opts
  let officeUuid = opts.officeUuid
  let adviserIds = orderedAdviserIdsFromBlok(blok)

  const fetchAdvisersByOffice = async (): Promise<AdviserStory[]> => {
    const sbParams = {
      ...searchParamsWithLocale(),
      filter_query: {
        component: { in: 'adviser' },
        offices: { any_in_array: officeUuid },
        __or: [
          { 'settings.location_available': { in: location } },
          { 'settings.location_available': { not_in: filterLocation } },
        ],
      },
      per_page: 100,
    }
    const { data } = await storyblokApi.get(`cdn/stories/`, sbParams)
    return (data?.stories ?? []) as AdviserStory[]
  }

  const passesLocation = (story: AdviserStory) => {
    const raw = story.content?.settings
    const v = Array.isArray(raw) ? raw[0]?.location_available : undefined
    if (v == null) return true
    const av = Array.isArray(v) ? v.map(String) : [String(v)]
    return av.includes(location) || !av.includes(filterLocation)
  }

  const linksOffice = (story: AdviserStory, targetUuid: string) => {
    const offices = story.content?.offices
    if (!Array.isArray(offices) || !offices.length) return false
    const t = String(targetUuid)
    return offices.some((o: unknown) => {
      if (o == null) return false
      if (typeof o === 'string') return String(o) === t
      if (typeof o === 'object' && o && 'uuid' in o)
        return String((o as { uuid: string }).uuid) === t
      return false
    })
  }

  try {
    const slug = storySlugFromPathname(opts.pathname || '')
    if (slug) {
      try {
        const { data } = await storyblokApi.get(`cdn/stories/${slug}`, searchParamsWithLocale())
        const story = data?.story
        if (story?.uuid) officeUuid = story.uuid
        if (!adviserIds.length) adviserIds = orderedAdviserIdsFromPageBody(story?.content)
      } catch {
        /* use opts.officeUuid */
      }
    }

    if (!adviserIds.length) return await fetchAdvisersByOffice()

    const sbParams = {
      ...searchParamsWithLocale(),
      by_uuids_ordered: adviserIds.join(','),
      per_page: 100,
    }

    const { data } = await storyblokApi.get(`cdn/stories/`, sbParams)
    const stories = (data?.stories ?? []) as AdviserStory[]
    const byUuid = new Map<string, AdviserStory>(stories.map((story) => [story.uuid, story]))

    const pickOrdered = (pred: (story: AdviserStory) => boolean) =>
      adviserIds
        .map((id) => byUuid.get(id))
        .filter((story): story is AdviserStory => story != null && pred(story))

    const strict = pickOrdered((s) => linksOffice(s, officeUuid) && passesLocation(s))
    if (strict.length) return strict

    const loose = pickOrdered((s) => passesLocation(s))
    if (loose.length) return loose

    return await fetchAdvisersByOffice()
  } catch (error) {
    console.error('Failed to fetch adviser data:', error)
    try {
      return await fetchAdvisersByOffice()
    } catch {
      return []
    }
  }
}
